import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    SelectChangeEvent,
    FormControlLabel,
    Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BaseDialog from '../common/BaseDialog';
import { businessService, AddUserDto, UpdateUserDto } from '../../services/businessService';
import { BusinessUser } from '../../types/business';
import { businessRoleService, BusinessRole } from '../../services/businessRoleService';
import { appointmentTypeShareService } from '../../services/appointmentTypeShareService';
import { appointmentTypeService, AppointmentType } from '../../services/appointmentTypeService';
import { sortUsers } from '../../utils/userHelpers';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface BusinessUsersTabProps {
    businessId: string;
}

interface FormData {
    email: string;
    firstName: string;
    lastName: string;
    businessRole: {
        roleId: string;
        name: string;
    };
}

const initialFormData: FormData = {
    email: '',
    firstName: '',
    lastName: '',
    businessRole: {
        roleId: '',
        name: ''
    }
};

// Field order for consistent error message ordering (matches validator rule order)
const userFieldOrder = ['Email', 'FirstName', 'LastName', 'BusinessRole.RoleId', 'BusinessRole.Name'];

const BusinessUsersTab: React.FC<BusinessUsersTabProps> = ({ businessId }) => {
    const [users, setUsers] = useState<BusinessUser[]>([]);
    const [roles, setRoles] = useState<BusinessRole[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<BusinessUser | null>(null);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [newUserCredentials, setNewUserCredentials] = useState<{
        email: string;
        password: string;
    } | null>(null);

    const loadUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await businessService.getBusinessUsers(businessId);
            setUsers(sortUsers(data));
        } catch (err) {
            setError('Failed to load users. Please try again later.');
            console.error('Error loading users:', err);
        } finally {
            setIsLoading(false);
        }
    }, [businessId]);

    const loadRoles = useCallback(async () => {
        try {
            const data = await businessRoleService.getBusinessRoles(businessId);
            setRoles(data);
        } catch (err) {
            console.error('Error loading roles:', err);
            setError('Failed to load roles');
        }
    }, [businessId]);

    useEffect(() => {
        if (businessId) {
            loadUsers();
            loadRoles();
        }
    }, [businessId, loadUsers, loadRoles]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        const selectedRole = roles.find(role => role.id === event.target.value);
        setFormData(prev => ({
            ...prev,
            businessRole: {
                roleId: event.target.value,
                name: selectedRole?.name || ''
            }
        }));
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        setDialogError(null);

        try {
            if (editingUser) {
                // Update existing user
                try {
                    await businessService.updateBusinessUser(editingUser.id, {
                        email: editingUser.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        // Only include businessRole if a role is actually selected
                        businessRole: formData.businessRole.roleId ? formData.businessRole : undefined
                    });
                    setSuccess('User updated successfully');
                } catch (error: any) {
                    setDialogError(extractApiErrorMessage(error, 'An unexpected error occurred', userFieldOrder));
                    setIsLoading(false);
                    return;
                }
            } else {
                // Create new user
                try {
                    const response = await businessService.addBusinessUser({
                        email: formData.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        // Only include businessRole if a role is actually selected
                        // Empty string for roleId cannot be deserialized to Guid
                        businessRole: formData.businessRole.roleId ? formData.businessRole : null
                    });

                    // If we're in development mode and got credentials back, save them
                    if (response.initialPassword) {
                        setNewUserCredentials({
                            email: response.user.email,
                            password: response.initialPassword
                        });
                    }
                    setSuccess('User added successfully');
                } catch (error: any) {
                    setDialogError(extractApiErrorMessage(error, 'An unexpected error occurred', userFieldOrder));
                    setIsLoading(false);
                    return;
                }
            }

            setFormData(initialFormData);
            setDialogError(null);
            setIsDialogOpen(false);
            await loadUsers();
        } catch (error: any) {
            console.error('Error saving user:', error);
            setDialogError(extractApiErrorMessage(error, 'An unexpected error occurred', userFieldOrder));
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (user: BusinessUser) => {
        // Find the role ID that matches the user's role
        const userRole = user.businessRoles[0]?.role || '';
        const matchedRole = roles.find(role => role.name === userRole);

        // If we can't find a matching role, try to use the first available role
        // This handles cases where the role name might have changed or roles haven't fully loaded
        const roleId = matchedRole?.id || (roles.length > 0 ? roles[0].id : '');
        const roleName = matchedRole?.name || (roles.length > 0 ? roles[0].name : '');

        if (!roleId) {
            setError('Unable to edit user: roles have not loaded yet. Please try again.');
            return;
        }

        setEditingUser(user);
        setFormData({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            businessRole: {
                roleId: roleId,
                name: roleName || userRole
            }
        });
        setDialogError(null);
        setIsDialogOpen(true);
    };

    const handleDelete = async (user: BusinessUser) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            await businessService.deleteBusinessUser(user.id);
            setSuccess('User deleted successfully');
            await loadUsers();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            setError(extractApiErrorMessage(error, 'An unexpected error occurred', userFieldOrder));
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setEditingUser(null);
        setFormData(initialFormData);
        setDialogError(null);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setEditingUser(null);
        setFormData(initialFormData);
        setDialogError(null);
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {newUserCredentials && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body1">New user credentials (for development only):</Typography>
                    <Typography variant="body2">Email: {newUserCredentials.email}</Typography>
                    <Typography variant="body2">Password: {newUserCredentials.password}</Typography>
                </Alert>
            )}

            <Button
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
                sx={{ mb: 2 }}
            >
                Add New User
            </Button>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.businessRoles[0]?.role || 'No Role'}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEdit(user)} disabled={isLoading}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(user)} disabled={isLoading}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <BaseDialog 
                open={isDialogOpen} 
                onClose={handleCloseDialog} 
                title={editingUser ? 'Edit User' : 'Add New User'}
                fullWidth
                actions={
                    <>
                        <Button 
                            onClick={handleCloseDialog}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={isLoading}
                        >
                            {editingUser ? 'Update' : 'Add'}
                        </Button>
                    </>
                }
            >
                    <Box component="form" sx={{ mt: 2 }}>
                        {dialogError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{dialogError}</Alert>}
                        <TextField
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            disabled={!!editingUser}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            name="firstName"
                            label="First Name"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            name="lastName"
                            label="Last Name"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.businessRole.roleId}
                                onChange={handleRoleChange}
                                label="Role"
                                required
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
            </BaseDialog>
        </Box>
    );
};

export default BusinessUsersTab; 